/** The One-off payslips page's decisions, kept pure because vitest cannot render a component. */
import type { MailOutcome, OneOff, OneOffDraft, OneOffLine, OneOffLineKind, OneOffStatus } from '../../lib/oneOff'

/** The English label close-out stores on a leaver's line (`services/one_off_payslip.py::LEAVER_LABEL`). */
export const LEAVER_LABEL = 'Leave payout on leaving'

/** The kinds a person may type in; a leave payout line comes only from a close-out. */
export const MANUAL_KINDS: readonly OneOffLineKind[] = ['bonus', 'correction', 'other']

export type OneOffAction = 'edit' | 'issue' | 'discard' | 'mail' | 'void' | 'pdf'

/** What a viewer may do to a one-off: the PDF always; changes only with `payroll`, and never on their own. A leaver's reissued draft pays a payout already paid, so it is never discarded. */
export function oneOffActions(
  status: OneOffStatus,
  { mayPay, isSelf, leaverReissue = false }: { mayPay: boolean; isSelf: boolean; leaverReissue?: boolean },
): OneOffAction[] {
  if (!mayPay) return ['pdf']
  if (status === 'Draft') return isSelf ? ['pdf'] : leaverReissue ? ['edit', 'issue', 'pdf'] : ['edit', 'issue', 'discard', 'pdf']
  if (status === 'Issued') return isSelf ? ['mail', 'pdf'] : ['mail', 'void', 'pdf']
  return ['pdf']
}

/** A leaver's issued one-off pays their payout, so voiding it always reissues a draft in its place. */
export function voidMustReissue(row: Pick<OneOff, 'status' | 'resignation_payout_id'>): boolean {
  return row.status === 'Issued' && row.resignation_payout_id != null
}

const AMOUNT = /^-?\d+(\.\d{1,2})?$/

export type LineProblem = 'labelMissing' | 'amountInvalid' | 'negativeOnlyCorrection'

/** Why the server would refuse this line, or null. */
export function lineProblem(line: OneOffLine): LineProblem | null {
  if (!line.label.trim()) return 'labelMissing'
  const amount = line.amount.replace(/,/g, '').trim()
  if (!AMOUNT.test(amount)) return 'amountInvalid'
  if (Number(amount) < 0 && line.kind !== 'correction') return 'negativeOnlyCorrection'
  return null
}

export type DraftProblem = 'personMissing' | 'dateMissing' | 'noLines' | LineProblem

export function draftProblem(draft: OneOffDraft): DraftProblem | null {
  if (!draft.employee_id) return 'personMissing'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.pay_date)) return 'dateMissing'
  if (!draft.lines.length) return 'noLines'
  for (const line of draft.lines) {
    const problem = lineProblem(line)
    if (problem) return problem
  }
  return null
}

/** The draft as the server takes it, or null while it would be refused -- and null previews nothing. */
export function cleanDraft(draft: OneOffDraft): OneOffDraft | null {
  if (draftProblem(draft)) return null
  return {
    ...draft,
    lines: draft.lines.map((line) => ({ ...line, label: line.label.trim(), amount: line.amount.replace(/,/g, '').trim() })),
  }
}

/** A stored fraction as a person reads it: `0.1150` -> `11.5%`. */
export function percentText(fraction: string | number): string {
  const value = Math.round(Number(fraction) * 100 * 100) / 100
  return `${Number.isFinite(value) ? value : 0}%`
}

/** A cap that is not set is "no cap", never a zero amount. */
export function capShown(cap: string | null): { kind: 'none' } | { kind: 'amount'; value: string } {
  return cap == null ? { kind: 'none' } : { kind: 'amount', value: cap }
}

/** The line's description in the reader's language when it is the stored leaver label. */
export function lineLabel(line: OneOffLine, t: (key: string) => string): string {
  return line.kind === 'leave_payout' && line.label === LEAVER_LABEL ? t('oneOff.leaverLabel') : line.label
}

/** What a Send press did, in the reader's words; a refusal the server names is translated. */
export function sendNotice(
  outcome: MailOutcome,
  t: (key: string, options?: Record<string, string>) => string,
): { tone: 'success' | 'info' | 'warning'; text: string } {
  if (outcome.suppressed) return { tone: 'info', text: t('oneOff.mailSuppressed') }
  if (outcome.queued)
    return {
      tone: 'success',
      text: outcome.address === 'personal' ? t('oneOff.mailQueuedPersonal', { to: outcome.to }) : t('oneOff.mailQueued', { to: outcome.to }),
    }
  if (outcome.already_sent) return { tone: 'info', text: t('oneOff.mailAlready') }
  const reason = outcome.skipped[0]?.reason ?? ''
  const why =
    reason === 'no personal address'
      ? t('oneOff.noPersonalAddress')
      : reason === 'no e-mail address'
        ? t('oneOff.noAddress')
        : reason === 'not issued'
          ? t('oneOff.notIssued')
          : reason
  return { tone: 'warning', text: t('oneOff.mailSkipped', { reason: why }) }
}

/** The issued one-offs in a month: what its bank file would carry. */
export function issuedInMonth(rows: OneOff[], year: number, month: number): OneOff[] {
  return rows.filter((row) => row.status === 'Issued' && row.year === year && row.month === month)
}

/** A new draft's opening state: a single blank bonus line. */
export function blankDraft(payDate: string): OneOffDraft {
  return { employee_id: '', pay_date: payDate, lines: [{ kind: 'bonus', label: '', amount: '' }], reason: '' }
}

/** A saved Draft opened for editing, as the form holds it. */
export function draftOf(row: OneOff): OneOffDraft {
  return {
    employee_id: row.employee_id,
    pay_date: row.pay_date,
    lines: row.lines.map((line) => ({ ...line, amount: String(line.amount) })),
    reason: row.reason,
  }
}
